'use client'

import { useState } from 'react'
import type { BackofficeUser } from 'api/generated'
import { ConfirmDialog } from 'components/confirm-dialog'
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { showSubmittedData } from 'helpers/show-submitted-data'
import { AlertTriangle } from 'lucide-react'
import { useAppDispatch } from 'store/store'
import { deleteUser } from 'store/users/usersSlice'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow: BackofficeUser
}

export function UsersDeleteDialog({ open, onOpenChange, currentRow }: Props) {
  const [value, setValue] = useState('')
  const dispatch = useAppDispatch()

  const handleDelete = () => {
    if (value.trim() !== currentRow.email) return

    onOpenChange(false)
    showSubmittedData(currentRow, 'The following user has been deleted:')
    dispatch(deleteUser(currentRow.id))
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      form='users-delete-form'
      disabled={value.trim() !== currentRow.email}
      title={
        <span className='text-destructive'>
          <AlertTriangle
            className='me-1 inline-block stroke-destructive'
            size={18}
          />{' '}
          Delete User
        </span>
      }
      desc={
        <form
          id='users-delete-form'
          onSubmit={(e) => {
            e.preventDefault()
            handleDelete()
          }}
          className='space-y-4'
        >
          <p className='mb-2'>
            Are you sure you want to delete{' '}
            <span className='font-bold'>{currentRow.email}</span>?
            <br />
            This action will permanently remove the user with the id of{' '}
            <span className='font-bold'>{currentRow.id}</span> from the system.
            This cannot be undone.
          </p>

          <Label className='my-2'>
            Username:
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder='Enter username to confirm deletion.'
              autoFocus
            />
          </Label>

          <Alert variant='destructive'>
            <AlertTitle>Warning!</AlertTitle>
            <AlertDescription>
              Please be careful, this operation can not be rolled back.
            </AlertDescription>
          </Alert>
        </form>
      }
      confirmText='Delete'
      destructive
    />
  )
}
